import {
  Form,
  Input,
  Select,
  Tag,
  DatePicker,
  Button,
  Row,
  Col,
  notification
} from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons/lib';
import { useTranslation } from 'react-i18next';
import { secondaryProfileInformations } from './secondaryProfileInformationFields';
import { compressFile } from '../../../utils/filesManagement/compressFile';
import { createPhoneNumberFieldConfig } from '../../../utils/formFields/phoneNumberField';
import { createDocumentDraggerField } from '../../../utils/formFields/documentDraggerField';

const { Option } = Select;

/**
 * Personal Informations Fields
 * @hook
 * @typedef {Object} PersonalInformationsFields
 * @property {function} setFilesList - Function to set the files list.
 * @property {Array} filesList - List of files.
 * @property {Object} enums - Object containing enums.
 * @property {boolean} isFieldsLoading - Flag indicating if fields are loading.
 * @property {function} setShowSecondProfile - Function to set the showSecondProfile flag.
 * @property {boolean} showSecondProfile - Flag indicating if the second profile is shown.
 * @property {function} setRemovedFiles - Function to set the removed files.
 *
 * @returns {Object} - Object containing the personal informations fields.
 */
export const personalInformations = ({
  setFilesList,
  filesList,
  enums,
  isFieldsLoading,
  setShowSecondProfile,
  showSecondProfile,
  setRemovedFiles
}) => {
  const { t } = useTranslation();

  const filteredEnums = enums?.civility?.filter(
    (civility) => !civility.includes('&')
  );

  const { secondaryProfileInformationsFields } = secondaryProfileInformations({
    setFilesList,
    filesList,
    filteredEnums,
    isFieldsLoading,
    setRemovedFiles
  });

  const draggerProps = (name) => ({
    onRemove: () => {
      const updatedFileList = { ...filesList };
      setRemovedFiles((prevRemovedFiles) => [
        ...prevRemovedFiles,
        updatedFileList[name]?.[0]?.id || updatedFileList[name]?.[0]?._id
      ]);
      if (name === 'identification_photo') {
        updatedFileList[name] = [];
      } else {
        delete updatedFileList[name];
      }
      setFilesList(updatedFileList);
    },
    beforeUpload: async (file) => {
      const newFile = file;
      const fileExtension = file.name.split('.').pop();

      if (name === 'identification_photo' && fileExtension !== 'pdf') {
        const compressedFile = await compressFile(file);
        const reader = new FileReader();
        reader.onload = (e) => {
          newFile.url = e.target.result;
        };
        reader.readAsDataURL(file);
        setFilesList((prevFileList) => ({
          ...prevFileList,
          [name]: [compressedFile]
        }));
        return false;
      }
      if (name !== 'identification_photo') {
        setFilesList((prevFileList) => ({
          ...prevFileList,
          [name]: [newFile]
        }));

        return false;
      }

      notification.warning({
        message: t('messages.file_extension_warning', {
          extensions: `png, jpg, jpeg, ${
            name !== 'identification_photo' ? 'pdf' : ''
          }`
        }),
        duration: 5
      });
      return true;
    },
    // fix: fallback [] si le nom n'existe pas encore dans filesList
    fileList: filesList[name] ?? []
  });

  const toggleSecondProfile = () => {
    setShowSecondProfile(!showSecondProfile);
  };

  const personnalInformationsFields = [
    {
      name: ['civility'],
      rules: [{ required: true }],
      input: (
        <Select loading={isFieldsLoading}>
          {(filteredEnums || []).map((civility) => (
            <Option key={civility} value={civility}>
              <Tag>{t(`pensioners.tags.${civility}`)}</Tag>
            </Option>
          ))}
        </Select>
      )
    },
    {
      name: ['last_name'],
      rules: [{ required: true }]
    },
    {
      name: ['first_name'],
      rules: [{ required: true }]
    },
    {
      name: ['birth_date'],
      input: <DatePicker format="DD/MM/YYYY" />
    },
    {
      name: ['profession']
    },
    {
      name: ['principal_language']
    },
    createPhoneNumberFieldConfig('cell_phone_number', 80),
    {
      label: 'other_languages.label',
      input: (
        <Form.List name={['other_languages']}>
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ name, key }) => (
                <Row
                  justify="space-between"
                  gutter={4}
                  key={key}
                  style={{ marginBottom: '4px' }}
                >
                  <Col span={20}>
                    <Form.Item noStyle name={[name]}>
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col span={4}>
                    <Button onClick={() => remove(name)}>
                      <CloseOutlined />
                    </Button>
                  </Col>
                </Row>
              ))}
              <Button
                type="dashed"
                style={{ marginTop: '4px', width: '100%' }}
                onClick={() => {
                  add();
                }}
                icon={<PlusOutlined />}
              >
                {t('pensioners.form.other_languages.add_button')}
              </Button>
            </>
          )}
        </Form.List>
      )
    },
    createDocumentDraggerField('criminal_record_extract', draggerProps, t),
    createDocumentDraggerField('identification_photocopy', draggerProps, t),
    createDocumentDraggerField('identification_photo', draggerProps, t),
    {
      noLabel: true,
      hidden: !showSecondProfile,
      name: [''],
      input: (
        <Button onClick={toggleSecondProfile}>
          {t('pensioners.form.buttons.cancel')}
        </Button>
      )
    },
    ...(showSecondProfile ? secondaryProfileInformationsFields : []),
    {
      noLabel: true,
      hidden: showSecondProfile,
      name: [''],
      input: (
        <Button type="dashed" onClick={toggleSecondProfile} block>
          {t('pensioners.form.buttons.add_profile')}
        </Button>
      )
    }
  ];

  return { personnalInformationsFields };
};
